import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

// Add request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    console.log('Making API request:', config.method?.toUpperCase(), config.url, config.params);
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
api.interceptors.response.use(
  (response) => {
    console.log('API response received:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('API error:', error.response?.status, error.response?.data || error.message);
    if (error.code === 'ERR_NETWORK') {
      console.error('Network error - check if backend is running on port 8000');
    }
    return Promise.reject(error);
  }
);

export interface MedicalRecord {
  id: number;
  record_date: string;
  record_time: string | null;
  patient_name: string | null;
  doctor_name: string | null;
  hospital_name: string | null;
  procedure_name: string | null;
  procedure_description: string | null;
  insurance_name: string | null;
  amount_paid: number | null;
  // Billing: the spreadsheets record the gross surgery value; *_net is the
  // doctor's share after the retention.
  amount_billed: number | null;
  amount_received: number | null;
  amount_billed_net: number | null;
  amount_received_net: number | null;
  doctor_share_pct: number | null;
  payment_received_at: string | null;
  payment_confirmed: boolean;
  payment_lag_days: number | null;
  /** Derived in the database so list, filters and alerts cannot disagree. */
  payment_state: PaymentState | null;
  days_since_procedure: number | null;
  source_file: string | null;
  record_type: 'appointment' | 'payment';
  comments: string | null;
  email_id: string | null;
  processing_date: string;
  duration: number | null;
  material_cost: number | null;
  travel_expenses: number | null;
  created_at: string;
  updated_at: string;
}

/** paid | scheduled | awaiting (<3mo) | due (3-6mo) | overdue (6-12mo) | critical (>12mo) */
export type PaymentState =
  | 'paid'
  | 'scheduled'
  | 'awaiting'
  | 'due'
  | 'overdue'
  | 'critical'
  | 'unknown';

export interface Statistics {
  total_records: number;
  total_appointments: number;
  total_payments: number;
  total_amount: number;
  unique_patients: number;
  unique_doctors: number;
  unique_hospitals: number;
  date_range: {
    start: string | null;
    end: string | null;
  };
}

export interface DoctorStats {
  doctor_name: string;
  appointment_count: number;
  total_amount: number;
  patient_count: number;
}

export interface HospitalStats {
  hospital_name: string;
  appointment_count: number;
  total_amount: number;
  doctor_count: number;
}

export interface MonthlyData {
  month: number;
  record_count: number;
  appointments: number;
  payments: number;
  total_amount: number;
}

export interface MonthlySummary {
  year: number;
  months: MonthlyData[];
}

// API functions
export const getRecords = async (params?: {
  limit?: number;
  offset?: number;
  search?: string;
  record_type?: string;
  start_date?: string;
  end_date?: string;
  /** 'unpaid' rolls up every state that is not settled. */
  payment_state?: PaymentState | 'unpaid';
}): Promise<MedicalRecord[]> => {
  try {
    console.log('API call to /api/records with params:', params);
    const response = await api.get('/api/records', { params });
    console.log('API response:', response.status, response.data?.length || 0, 'records');
    return response.data || [];
  } catch (error: any) {
    console.error('API call failed:', error.response?.data || error.message);
    if (error.response?.status === 500) {
      throw new Error('Server error - please check if the backend is running');
    }
    throw error;
  }
};

export const getStatistics = async (): Promise<Statistics> => {
  const response = await api.get('/api/statistics');
  return response.data;
};

// `months` restricts the stats to records from the last N months.
// Pass undefined for all time.
export const getDoctorStats = async (
  limit: number = 20,
  months?: number
): Promise<DoctorStats[]> => {
  const response = await api.get('/api/doctors/stats', { params: { limit, months } });
  return response.data;
};

export const getHospitalStats = async (
  limit: number = 20,
  months?: number
): Promise<HospitalStats[]> => {
  const response = await api.get('/api/hospitals/stats', { params: { limit, months } });
  return response.data;
};

export const getMonthlySummary = async (year?: number): Promise<MonthlySummary> => {
  const response = await api.get('/api/monthly-summary', { params: { year } });
  return response.data;
};

export const getPatients = async (search?: string): Promise<any[]> => {
  const response = await api.get('/api/patients', { params: { search } });
  return response.data;
};

export const getDoctors = async (): Promise<any[]> => {
  const response = await api.get('/api/doctors');
  return response.data;
};

export const getHospitals = async (): Promise<any[]> => {
  const response = await api.get('/api/hospitals');
  return response.data;
};

// CRUD operations for medical records
export interface CreateRecordData {
  record_date: string;
  record_time?: string;
  patient_name?: string;
  doctor_name?: string;
  hospital_name?: string;
  procedure_name?: string;
  procedure_description?: string;
  insurance_name?: string;
  amount_paid?: number;
  record_type: 'appointment' | 'payment';
  comments?: string;
  duration?: number;
  material_cost?: number;
  travel_expenses?: number;
}

export interface UpdateRecordData {
  record_date?: string;
  record_time?: string;
  patient_name?: string;
  doctor_name?: string;
  hospital_name?: string;
  procedure_name?: string;
  procedure_description?: string;
  insurance_name?: string;
  amount_paid?: number;
  record_type?: 'appointment' | 'payment';
  comments?: string;
  duration?: number;
  material_cost?: number;
  travel_expenses?: number;
}

export const createRecord = async (recordData: CreateRecordData): Promise<{ success: boolean; id: number; message: string }> => {
  const response = await api.post('/api/records', recordData);
  return response.data;
};

export const getRecord = async (id: number): Promise<MedicalRecord> => {
  const response = await api.get(`/api/records/${id}`);
  return response.data;
};

export const updateRecord = async (id: number, recordData: UpdateRecordData): Promise<{ success: boolean; message: string }> => {
  const response = await api.put(`/api/records/${id}`, recordData);
  return response.data;
};

export const deleteRecord = async (id: number): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`/api/records/${id}`);
  return response.data;
};

// Payment interfaces
export interface PaymentRecord {
  id: number;
  payment_date: string;
  doctor_name: string;
  hospital_name: string | null;
  patient_name: string;
  insurance_company: string | null;
  invoice_number: string | null;
  amount_paid: number;
  document_reference: string | null;
  source_file: string | null;
  medical_record_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface PaymentStats {
  total_payments: number;
  total_amount: number;
  matched_payments: number;
  unmatched_payments: number;
}

// Payment API functions
export const getPayments = async (params?: {
  limit?: number;
  offset?: number;
}): Promise<PaymentRecord[]> => {
  try {
    console.log('API call to /api/payments with params:', params);
    const response = await api.get('/api/payments', { params });
    console.log('API response:', response.status, response.data?.length || 0, 'payments');
    return response.data || [];
  } catch (error: any) {
    console.error('Payment API call failed:', error.response?.data || error.message);
    throw error;
  }
};

export const getPaymentStats = async (): Promise<PaymentStats> => {
  const response = await api.get('/api/payments/stats');
  return response.data;
};

export const matchPayments = async (): Promise<{ success: boolean; matched_count: number }> => {
  const response = await api.post('/api/payments/match');
  return response.data;
};

export const uploadPaymentFile = async (file: File): Promise<{ success: boolean; message: string; imported_count: number; file_date: string }> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post('/api/payments/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/** Outcome for a single file within a bulk upload. */
export interface BulkUploadFileResult {
  filename: string;
  success: boolean;
  imported_count: number;
  /** Rows skipped because that payment was already recorded. */
  duplicate_count?: number;
  file_date?: string;
  message?: string;
  error?: string;
}

export interface BulkUploadResult {
  success: boolean;
  files_received: number;
  files_succeeded: number;
  files_failed: number;
  total_imported: number;
  total_duplicates?: number;
  message: string;
  results: BulkUploadFileResult[];
}

/**
 * Upload several repasse spreadsheets in one request.
 *
 * Each file is processed independently server-side, so a single malformed
 * spreadsheet reports an error against itself rather than discarding the rest
 * of the batch. Callers should surface the per-file `results`.
 */
export const uploadPaymentFilesBulk = async (files: File[]): Promise<BulkUploadResult> => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));

  const response = await api.post('/api/payments/upload-bulk', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// Hospital Master Data interfaces
export interface HospitalMaster {
  id: number;
  name: string;
  short_name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  cnpj: string | null;
  contact_person: string | null;
  contact_phone: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface HospitalMasterCreate {
  name: string;
  short_name?: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  phone?: string;
  email?: string;
  website?: string;
  cnpj?: string;
  contact_person?: string;
  contact_phone?: string;
  notes?: string;
  is_active?: boolean;
}

// User Management interfaces
export interface User {
  id: number;
  username: string;
  name: string;
  email: string | null;
  cpf: string | null;
  rg: string | null;
  crm: string | null;
  user_type: 'doctor' | 'patient' | 'admin' | 'user';
  address: string | null;
  phone: string | null;
  is_active: boolean;
  last_login: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserCreate {
  username: string;
  password: string;
  name: string;
  email?: string;
  cpf?: string;
  rg?: string;
  crm?: string;
  user_type: 'doctor' | 'patient' | 'admin' | 'user';
  address?: string;
  phone?: string;
  is_active?: boolean;
}

export interface LoginRequest {
  username: string;
  password: string;
}

// Hospital Master Data API functions
export const getHospitalMaster = async (params?: {
  limit?: number;
  offset?: number;
  search?: string;
  active_only?: boolean;
}): Promise<HospitalMaster[]> => {
  const response = await api.get('/api/hospital-master', { params });
  return response.data;
};

export const createHospitalMaster = async (hospital: HospitalMasterCreate): Promise<{ success: boolean; id: number; message: string }> => {
  const response = await api.post('/api/hospital-master', hospital);
  return response.data;
};

export const getHospitalMasterById = async (id: number): Promise<HospitalMaster> => {
  const response = await api.get(`/api/hospital-master/${id}`);
  return response.data;
};

export const updateHospitalMaster = async (id: number, hospital: Partial<HospitalMasterCreate>): Promise<{ success: boolean; message: string }> => {
  const response = await api.put(`/api/hospital-master/${id}`, hospital);
  return response.data;
};

export const deleteHospitalMaster = async (id: number): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`/api/hospital-master/${id}`);
  return response.data;
};

// User Management API functions
export const login = async (credentials: LoginRequest): Promise<{ success: boolean; user: any; message: string }> => {
  const response = await api.post('/api/auth/login', credentials);
  return response.data;
};

export const getUsers = async (params?: {
  limit?: number;
  offset?: number;
  search?: string;
  user_type?: string;
  active_only?: boolean;
}): Promise<User[]> => {
  const response = await api.get('/api/users', { params });
  return response.data;
};

export const createUser = async (user: UserCreate): Promise<{ success: boolean; id: number; message: string }> => {
  const response = await api.post('/api/users', user);
  return response.data;
};

export const getUserById = async (id: number): Promise<User> => {
  const response = await api.get(`/api/users/${id}`);
  return response.data;
};

export const updateUser = async (id: number, user: Partial<UserCreate>): Promise<{ success: boolean; message: string }> => {
  const response = await api.put(`/api/users/${id}`, user);
  return response.data;
};

export const deleteUser = async (id: number): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`/api/users/${id}`);
  return response.data;
};

// ---------------------------------------------------------------------------
// Payment confirmation and surgery document attachments
// ---------------------------------------------------------------------------

export interface PaymentConfirmationResult {
  success: boolean;
  amount_billed: number | null;
  amount_received: number;
  amount_outstanding: number | null;
  retained_pct: number | null;
  payment_lag_days: number | null;
}

/** Mark a record as paid: the tick, the amount and the date. */
export const confirmPayment = async (
  recordId: number,
  amountReceived: number,
  paymentReceivedAt?: string,
  notes?: string
): Promise<PaymentConfirmationResult> => {
  const response = await api.post(`/api/records/${recordId}/payment`, {
    amount_received: amountReceived,
    payment_received_at: paymentReceivedAt,
    notes,
  });
  return response.data;
};

/** etiqueta | folha de sala | TCLE | surgery report | other */
export type AttachmentKind = 'etiqueta' | 'folha_sala' | 'tcle' | 'relatorio' | 'outro';

export interface Attachment {
  id: number;
  medical_record_id: number;
  kind: AttachmentKind;
  original_filename: string;
  content_type: string | null;
  size_bytes: number | null;
  notes: string | null;
  uploaded_at: string;
}

export const getAttachments = async (recordId: number): Promise<Attachment[]> => {
  const response = await api.get(`/api/records/${recordId}/attachments`);
  return response.data;
};

/**
 * Attach a document. A record may carry several of the same kind -- more than
 * one surgery report is normal -- so this never replaces an existing file.
 */
export const uploadAttachment = async (
  recordId: number,
  kind: AttachmentKind,
  file: File,
  notes?: string
): Promise<Attachment> => {
  const formData = new FormData();
  formData.append('kind', kind);
  formData.append('file', file);
  if (notes) formData.append('notes', notes);

  const response = await api.post(`/api/records/${recordId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    // Scans are large and the default 10s client timeout is not enough.
    timeout: 120000,
  });
  return response.data;
};

export const deleteAttachment = async (attachmentId: number): Promise<{ success: boolean }> => {
  const response = await api.delete(`/api/attachments/${attachmentId}`);
  return response.data;
};

/** Absolute URL for downloading an attachment, for use in an anchor href. */
export const attachmentDownloadUrl = (attachmentId: number): string =>
  `${API_BASE_URL}/api/attachments/${attachmentId}/download`;

export interface OverdueRecord {
  id: number;
  record_date: string;
  patient_name: string | null;
  doctor_name: string | null;
  hospital_name: string | null;
  amount_billed: number | null;
  days_outstanding: number;
}

export const getOverdueRecords = async (months = 6, limit = 500): Promise<OverdueRecord[]> => {
  const response = await api.get('/api/records/overdue', { params: { months, limit } });
  return response.data;
};

export interface PaymentSummary {
  total_records: number;
  paid_records: number;
  total_billed: number;
  total_received: number;
  total_billed_net: number;
  total_received_net: number;
  outstanding_billed: number;
  outstanding_net: number;
  avg_payment_lag_days: number | null;
  max_payment_lag_days: number | null;
  total_travel_expenses: number;
  total_material_cost: number;
  overdue_count: number;
  overdue_billed: number;
  overdue_net: number;
  alert_threshold_months: number;
  settled_pct: number | null;
  doctor_share_pct: number;
}

export const getPaymentSummary = async (months = 6): Promise<PaymentSummary> => {
  const response = await api.get('/api/payments/summary', { params: { months } });
  return response.data;
};
